import dayjs from "dayjs";
import type { Simplify } from "type-fest";
import { z } from "zod";

import { makeMeasurand, makeSymbol } from "../../features/ems/domain";
import { makeOperatingState } from "../../features/ems/operating-states/domain";
import { makeAmbiguousChannel } from "../../features/ems/plan/domain/measuring-device";

function WithInlineETag<T extends z.ZodRawShape>(originalSchema: z.ZodObject<T>) {
  return z.object({
    ...(originalSchema.shape as z.ZodObject<T>["shape"]),
    _links: z.object({ self: z.object({ etag: z.string() }) }),
  });
}

//
// Response schemas (dto only)
//

export const EnergyFlowResponseSchema = z.object({
  unit: z.string(),
  prosumers: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      consumption: z.number(),
      production: z.number(),
      remainder: z.number(),
    })
  ),
  conduits: z.array(
    z.object({
      source: z.string(),
      sink: z.string(),
      forwardFlow: z.number(),
      returnFlow: z.number(),
    })
  ),
});
export type EnergyFlowDTO = z.infer<typeof EnergyFlowResponseSchema>;

const SymbolDTOResponseSchema = z.object({
  name: z.string(),
  subscript: z.string().optional(),
});

const MeasurandDTOResponseSchema = z.object({
  medium: z.string(),
  symbol: SymbolDTOResponseSchema,
});

const ConduitDTOResponseSchema = z.object({
  source: z.string(),
  sink: z.string(),
  medium: z.string(),
});

export const EnergySystemDTOResponseSchema = z
  .object({
    id: z.string(),
    conduits: z.array(ConduitDTOResponseSchema).readonly(),
  })
  .readonly();

export type EnergySystemDTO = z.infer<typeof EnergySystemDTOResponseSchema>;

const IntervalDTOSchema = z.preprocess(
  ({ interval, ...rest }: any) => ({
    ...rest,
    start: interval.split("/")[0],
    end: interval.split("/")[1],
  }),
  z.object({
    start: z.iso.datetime({ offset: true }),
    end: z.iso.datetime({ offset: true }),
  })
);

export const DataAcquisitionDTOResponseSchema = z.union([
  z.object({
    source: z.string(),
    interval: z.string(),
    measurand: MeasurandDTOResponseSchema,
  }),
  z.object({
    source: z.literal("calculated"),
    measurand: MeasurandDTOResponseSchema,
  }),
  z.object({
    measurand: MeasurandDTOResponseSchema,
  }),
]);

export type DataAcquisitionDTO = z.infer<typeof DataAcquisitionDTOResponseSchema>;

export const EnergyPricesSchema = z.object({
  unit: z.string(),
  segments: z.array(z.object({ value: z.number() }).and(IntervalDTOSchema)),
});
export type EnergyPricesDTO = z.infer<typeof EnergyPricesSchema>;

export const AccountingPointSchema = z.object({
  id: z.string(),
  name: z.string(),
  measurands: z.array(MeasurandDTOResponseSchema),
});
export type AccountingPointDTO = z.infer<typeof AccountingPointSchema>;

export const MediumDTOSchema = z.object({
  id: z.string(),
  type: z.enum(["default", "custom"]),
  label: z.string(),
  color: z.string(),
  dataAcquisitions: z.array(DataAcquisitionDTOResponseSchema),
});

export const MediumDTOResponseSchemaWithEtag = WithInlineETag(MediumDTOSchema).transform(
  ({ _links, ...rest }) => ({
    ...rest,
    etag: _links.self.etag,
  })
);
export type MediumDTO = Simplify<z.infer<typeof MediumDTOResponseSchemaWithEtag>>;

const ProsumerInputDTOResponseSchema = z.object({
  prosumerId: z.string(),
  allocationFactor: z.number(),
});

export const AreaDTOSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  prosumerInputs: z.array(ProsumerInputDTOResponseSchema),
});

export const AreaResponseSchemaWithEtag = WithInlineETag(AreaDTOSchema).transform(
  ({ _links, ...rest }) => ({
    ...rest,
    etag: _links.self.etag,
  })
);
export type AreaDTO = Simplify<z.infer<typeof AreaResponseSchemaWithEtag>>;
export type ProsumerInputDTO = z.infer<typeof ProsumerInputDTOResponseSchema>;

//
// Response schemas (dto -> domain)
//

export const MeasuringDeviceResponseSchema = z
  .object({
    label: z.string(),
    edgeDeviceId: z.string(),
    channels: z.array(
      z.object({
        sourceUri: z.string(),
        measurand: MeasurandDTOResponseSchema,
      })
    ),
    ambiguousChannels: z.optional(
      z.array(z.object({ sourceUri: z.string(), symbol: SymbolDTOResponseSchema }))
    ),
  })
  .transform((measuringDevice) => ({
    label: measuringDevice.label,
    edgeDeviceId: measuringDevice.edgeDeviceId,
    channels: measuringDevice.channels.map(({ sourceUri, measurand }) => {
      const medium = measurand.medium;
      const symbol = makeSymbol(measurand.symbol.name, measurand.symbol.subscript);
      return { sourceUri, measurand: makeMeasurand(medium, symbol) };
    }),
    ambiguousChannels: (measuringDevice.ambiguousChannels ?? []).map(({ sourceUri, symbol }) =>
      makeAmbiguousChannel(sourceUri, makeSymbol(symbol.name, symbol.subscript))
    ),
  }));

//
// Prosumer Aspect Schemas
//
const AnnotationAspectSchema = z.object({
  aspectId: z.string(),
  aspectType: z.literal("annotation"),
  text: z.string(),
});

const UtilizationAspectSchema = z.object({
  aspectId: z.string(),
  aspectType: z.literal("utilization"),
  nominalPower: z.number(),
  measurand: z.string(),
  optimalUtilizationMin: z.number(),
  optimalUtilizationMax: z.number(),
});

const MeterAspectSchema = z.object({
  aspectId: z.string(),
  aspectType: z.literal("meter"),
  medium: z.string(),
  model: z.string(),
  present: z.boolean(),
  installationLocation: z.string(),
  calibration: z.string(),
  mountingType: z.string(),
  protocol: z.string(),
  deviceAddress: z.string(),
});

const DeviceCommunicationAspectSchema = z.object({
  aspectId: z.string(),
  aspectType: z.literal("device_communication"),
  medium: z.string(),
  ipAddress: z.string(),
  netmask: z.string(),
  gatewayAddress: z.string(),
  serialInterface: z.string(),
  viaGateway: z.boolean(),
});

const CurrentTransducerAspectSchema = z.object({
  aspectId: z.string(),
  aspectType: z.literal("current_transducer"),
  transducerType: z.string(),
  conformityAssessment: z.string(),
  accuracyClass: z.string(),
  ratio: z.string(),
});

const VoltageTransducerAspectSchema = z.object({
  aspectId: z.string(),
  aspectType: z.literal("voltage_transducer"),
  ratio: z.string(),
});

const ElectricityConductorAspectSchema = z.object({
  aspectId: z.string(),
  aspectType: z.literal("electricity_conductor"),
  fuse: z.string(),
  diameter: z.string(),
  conductorCountPerPhase: z.string(),
});

const DocumentsAspectSchema = z.object({
  aspectId: z.string(),
  aspectType: z.literal("documents"),
  filenames: z.array(z.string()),
});

const AccountingExclusionAspectSchema = z.object({
  aspectId: z.string(),
  aspectType: z.literal("accounting_exclusion"),
});

export const ProsumerAspectResponseSchema = z.union([
  AnnotationAspectSchema,
  UtilizationAspectSchema,
  MeterAspectSchema,
  DeviceCommunicationAspectSchema,
  CurrentTransducerAspectSchema,
  VoltageTransducerAspectSchema,
  ElectricityConductorAspectSchema,
  DocumentsAspectSchema,
  AccountingExclusionAspectSchema,
]);

export const ProsumerAspectResponseSchemaWithEtag = z
  .discriminatedUnion("aspectType", [
    WithInlineETag(AnnotationAspectSchema),
    WithInlineETag(UtilizationAspectSchema),
    WithInlineETag(MeterAspectSchema),
    WithInlineETag(DeviceCommunicationAspectSchema),
    WithInlineETag(CurrentTransducerAspectSchema),
    WithInlineETag(VoltageTransducerAspectSchema),
    WithInlineETag(ElectricityConductorAspectSchema),
    WithInlineETag(DocumentsAspectSchema),
    WithInlineETag(AccountingExclusionAspectSchema),
  ])
  .transform(({ _links, ...rest }) => ({
    ...rest,
    etag: _links.self.etag,
  }));

export type AnnotationAspectDTO = Simplify<
  z.infer<typeof AnnotationAspectSchema> & { etag: string }
>;
export type UtilizationAspectDTO = Simplify<
  z.infer<typeof UtilizationAspectSchema> & { etag: string }
>;
export type MeterAspectDTO = Simplify<z.infer<typeof MeterAspectSchema> & { etag: string }>;
export type DeviceCommunicationAspectDTO = Simplify<
  z.infer<typeof DeviceCommunicationAspectSchema> & { etag: string }
>;
export type CurrentTransducerAspectDTO = Simplify<
  z.infer<typeof CurrentTransducerAspectSchema> & { etag: string }
>;
export type VoltageTransducerAspectDTO = Simplify<
  z.infer<typeof VoltageTransducerAspectSchema> & { etag: string }
>;
export type ElectricityConductorAspectDTO = Simplify<
  z.infer<typeof ElectricityConductorAspectSchema> & { etag: string }
>;
export type AccountingExclusionAspectDTO = Simplify<
  z.infer<typeof AccountingExclusionAspectSchema> & { etag: string }
>;
export type ProsumerAspectDTO = Simplify<
  z.infer<typeof ProsumerAspectResponseSchema> & { etag: string }
>;

export const ProsumerResponseSchema = z.object({
  id: z.string(),
  name: z.string(),
  dataAcquisitions: z.array(DataAcquisitionDTOResponseSchema),
  position: z.object({
    left: z.number(),
    top: z.number(),
  }),
  isVirtual: z.boolean(),
  aspects: z.array(ProsumerAspectResponseSchemaWithEtag).optional(),
});

export const ProsumerResponseSchemaWithEtag = WithInlineETag(ProsumerResponseSchema).transform(
  ({ _links, ...rest }) => ({
    ...rest,
    etag: _links.self.etag,
  })
);
export type ProsumerDTO = Simplify<z.infer<typeof ProsumerResponseSchemaWithEtag>>;

const DataPointSchema = z.object({
  time: z.iso.datetime({ offset: true }),
  value: z.number(),
});

export type DataPoint = z.infer<typeof DataPointSchema>;

export const LatestMeasurementsDataResponseSchema = z.object({
  unit: z.string(),
  dataPoint: DataPointSchema.optional(),
});

export type LatestMeasurementsData = z.infer<typeof LatestMeasurementsDataResponseSchema>;

const DeltaValueSchema = z.object({
  value: z.number(),
  containsNegativeDelta: z.boolean(),
});

export type DeltaValue = z.infer<typeof DeltaValueSchema>;

type AggregateTypeMap = {
  minimum: DataPoint | null;
  maximum: DataPoint | null;
  mean: { value: number } | null;
  delta: DeltaValue | null;
  raw: Array<DataPoint> | null;
  lttb: Array<DataPoint> | null;
};
export type Aggregate = keyof AggregateTypeMap;

type AggregationData<T extends Aggregate> = {
  [K in T]: AggregateTypeMap[K];
};

export type MeasurementsData<T extends Aggregate> = {
  unit: string;
  start: string;
  end: string;
  segments?: Array<{ start: string; end: string } & AggregationData<T>>;
} & AggregationData<T>;

export const MeasurementsDataResponseSchema = <T extends Aggregate>(
  requestedAggregates: Array<T>,
  withSegments: boolean
) => {
  const AggregationSchema = z.object({
    raw: DataPointSchema.array().nullable(),
    minimum: DataPointSchema.nullable(),
    maximum: DataPointSchema.nullable(),
    mean: z.object({ value: z.number() }).nullable(),
    delta: DeltaValueSchema.nullable(),
    lttb: DataPointSchema.array().nullable(),
  });

  const aggregatesToPick = requestedAggregates.map((key: Aggregate) => [key, true as const]);

  const BaseSchema = z
    .object({ unit: z.string() })
    .and(IntervalDTOSchema)
    .and(AggregationSchema.pick(Object.fromEntries(aggregatesToPick)));

  if (withSegments) {
    return BaseSchema.and(
      z.object({
        segments: IntervalDTOSchema.and(
          AggregationSchema.pick(Object.fromEntries(aggregatesToPick))
        ).array(),
      })
    );
  } else {
    return BaseSchema;
  }
};

export type AreaConsumptionDTO = {
  unit: string;
  start: string;
  end: string;
  delta: Record<string, number | null>;
  segments?: Array<{ start: string; end: string; delta: Record<string, number | null> }>;
};

export const AreaEnergyConsumptionResponseSchema = z
  .object({
    unit: z.string(),
    delta: z.record(z.string(), z.number().nullable()),
    segments: IntervalDTOSchema.and(
      z.object({ delta: z.record(z.string(), z.number().nullable()) })
    )
      .array()
      .optional(),
  })
  .and(IntervalDTOSchema)
  .transform(({ unit, delta, segments, start, end }): AreaConsumptionDTO => ({
    unit,
    start,
    end,
    delta,
    ...(segments && {
      segments: segments.map((segment) => ({
        start: segment.start,
        end: segment.end,
        delta: segment.delta,
      })),
    }),
  }));

const DurationSchema = z.string().transform((data) => dayjs.duration(data));

export const OperatingStatesResponseSchema = z
  .object({
    operatingStates: z.array(
      z
        .object({
          max: z.object({ value: z.number().nullable(), unit: z.string() }),
          time: DurationSchema,
        })
        .transform((data) => makeOperatingState(data.max.value, data.max.unit, data.time))
    ),
    loadDurationCurve: z.object({
      unit: z.string(),
      data: z.array(z.object({ time: DurationSchema, value: z.number().nullable() })),
    }),
  })
  .transform((data) => ({
    operatingStates: data.operatingStates,
    loadDuration: data.loadDurationCurve,
  }));

//
// Request schemas (domain -> dto)
//
const SymbolRequestSchema = z.object({
  name: z.string(),
  subscript: z.string().optional(),
});

export const MediumWriteRequestSchema = z.object({
  label: z.string(),
  color: z.string(),
});

export const HeatingValueWriteRequestSchema = z.object({
  value: z.number(),
});

export const MeasurementValueWriteRequestSchema = z.object({
  value: z.number(),
});

const ConduitRequestSchema = z.object({
  medium: z.string(),
  sink: z.string(),
  source: z.string(),
});

export const DataAcquisitionRequestSchema = z.union([
  z.object({
    source: z.string(),
    interval: z.string().refine((obj) => dayjs.isDuration(dayjs.duration(obj)), {
      message: "Interval must be a duration in iso format",
    }),
    measurand: z.object({
      medium: z.string(),
      symbol: SymbolRequestSchema,
    }),
  }),
  z.object({
    measurand: z.object({
      medium: z.string(),
      symbol: SymbolRequestSchema,
    }),
  }),
]);

export const PatchEnergySystemSchema = z
  .union([
    z.object({ op: z.literal("add"), path: z.literal("/conduits/-"), value: ConduitRequestSchema }),
    z.object({ op: z.literal("test"), path: z.string(), value: ConduitRequestSchema }),
    z.object({ op: z.literal("remove"), path: z.string() }),
  ])
  .array();

export type ConduitOperationDTO =
  | { operation: "add"; conduit: z.infer<typeof ConduitRequestSchema> }
  | { operation: "remove"; index: number; conduit: z.infer<typeof ConduitRequestSchema> };

const ProsumerInputRequestSchema = z.object({
  prosumerId: z.string(),
  allocationFactor: z.number().min(0).max(1),
});

export const AreaWriteRequestSchema = z.object({
  name: z.string().min(1),
  description: z.string(),
  prosumerInputs: z.array(ProsumerInputRequestSchema),
});
